import { Divider } from "@material-ui/core";
import { Card, ThemeProvider } from "@mui/material";
import { Col, Row } from "antd";
import LoadingScreen from "components/loading/LoadingScreen";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import moment from "moment";
import { useMemo, useState } from "react";
import { useGetListProvinceType } from "service/query/common.query";
import {
  useExportReportExtension,
  useGetReportExtension,
} from "service/query/report.query";
import muiTheme from "theme/muiTheme";

import ReportExtensionCard from "./components/ReportExtensionCard";
import ReportExtensionFilter from "./components/ReportExtensionFilter";
import classes from "./ReportExtension.module.css";

const ReportExension = () => {
  const [filter, setFilter] = useState(null);
  const [searchFormValues, setSearchFormValues] = useState(null);
  const { data: listProvinceType } = useGetListProvinceType();

  const { refetch: exportReportExtension, isFetching: isFetchingExport } =
    useExportReportExtension(
      {
        ...filter,
        exportFile: true,
      },
      { enabled: false }
    );

  const handleSubmit = (values) => {
    setFilter({
      fromDate: moment(values.dateRange[0]).startOf("day").toISOString(),
      toDate: moment(values.dateRange[1]).endOf("day").toISOString(),
      typeProvince: values.typeProvince,
      exportFile: false,
      code: values.code.includes(ALL_OPTION.value) ? [] : values.code,
    });
    setSearchFormValues(values);
  };

  const handleExport = () => {
    exportReportExtension();
  };

  const { data: reportExtenstionData, isFetching } = useGetReportExtension(
    filter,
    {
      enabled: !!filter,
    }
  );

  const isPostOfficeType = filter?.typeProvince === "postoffice";

  const provinceTypeTitle = useMemo(() => {
    if (!searchFormValues?.typeProvince) return;
    return listProvinceType.find(
      (item) => item.code === searchFormValues.typeProvince
    )?.name;
  }, [listProvinceType, searchFormValues?.typeProvince]);

  const columns = [
    {
      id: "contactCenterCode",
      name: "BĐT/TP",
    },
    {
      id: "contactCenterName",
      name: "Tên bưu cục",
    },
    {
      id: "extensionName",
      name: "Máy lẻ",
    },
    {
      id: "inbound",
      name: "Cuộc gọi vào",
      align: "center",
      children: [
        {
          id: "detailInboundCallExtension.totalCall",
          name: "Tổng",
          type: "number",
        },
        {
          id: "detailInboundCallExtension.totalAnsweredCall",
          name: "Trả lời",
          type: "number",
        },
        {
          id: isPostOfficeType
            ? "detailInboundCallExtension.totalBusyCall"
            : "detailInboundCallExtension.totalMissedCall",
          name: isPostOfficeType ? "Bận" : "Nhỡ",
          type: "number",
        },
        {
          id: isPostOfficeType
            ? "detailInboundCallExtension.totalNoAnsweredCall"
            : "detailInboundCallExtension.totalRejectedCall",
          name: isPostOfficeType ? "Không trả lời" : "Từ chối",
          type: "number",
        },
        {
          id: "detailInboundCallExtension.answeredRate",
          name: "Tỉ lệ trả lời",
          type: "percent",
        },
      ],
    },
    {
      id: "outbound",
      name: "Cuộc gọi ra",
      align: "center",
      children: [
        {
          id: "detailOutbountCallExtension.totalManualCall",
          name: "Thủ công",
          type: "number",
        },
        ...(isPostOfficeType
          ? [
              {
                id: "detailOutbountCallExtension.totalPackAndSendCall",
                name: "Gọi từ PNS",
                type: "number",
              },
            ]
          : []),
        {
          id: "detailOutbountCallExtension.totalCall",
          name: "Tổng",
          type: "number",
        },
        {
          id: "detailOutbountCallExtension.totalAnsweredCall",
          name: "Trả lời",
          type: "number",
        },
        {
          id: "detailOutbountCallExtension.totalNoAnsweredCall",
          name: "Không trả lời",
          type: "number",
        },
        {
          id: "detailOutbountCallExtension.totalBusyCall",
          name: "Bận",
          type: "number",
        },
        {
          id: "detailOutbountCallExtension.answeredRate",
          name: "Tỉ lệ trả lời",
          type: "percent",
        },
      ],
    },
    {
      id: "detailInboundCallExtension.totalTransferCall",
      name: isPostOfficeType ? "Chuyển đi" : "Chuyển đến",
      type: "number",
    },
  ];

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ p: 2 }}>
        <ReportExtensionFilter
          onSubmit={handleSubmit}
          onExport={handleExport}
          disabledExport={!filter}
        />
      </Card>

      <Card sx={{ p: 2, mt: 1 }}>
        <h3 className={classes.title}>
          Tổng đài {provinceTypeTitle}{" "}
          {searchFormValues?.dateRange?.[0] &&
            `(${moment(searchFormValues.dateRange[0]).format(
              "DD/MM/YYYY"
            )} - ${moment(searchFormValues.dateRange[1]).format(
              "DD/MM/YYYY"
            )})`}
        </h3>
        <Divider />
        <div className={classes.contactsContainer}>
          <Card className={classes.contactCenterUnit} sx={{ p: 1 }}>
            Đơn vị
          </Card>
          {searchFormValues && (
            <>
              {searchFormValues.code.includes(ALL_OPTION.value) ? (
                <span className={classes.contactCenterTitle}>Tất cả</span>
              ) : (
                searchFormValues.contactCenters.map((item, index) => (
                  <span className={classes.contactCenterTitle} key={index}>
                    {item.name}
                  </span>
                ))
              )}
            </>
          )}
        </div>

        <h3 className={classes.title}>Dữ liệu máy lẻ</h3>
        <Divider />

        <Row gutter={[16, 16]} className={classes.container}>
          <Col span={6}>
            <ReportExtensionCard
              title="Tổng số máy lẻ cài đặt"
              value={reportExtenstionData?.totalExtension}
            />
          </Col>
          <Col span={6}>
            <ReportExtensionCard
              title={
                isPostOfficeType
                  ? "Tổng số máy lẻ phát sinh cuộc gọi ra"
                  : "Tổng số máy lẻ phát sinh cuộc gọi"
              }
              value={reportExtenstionData?.totalExtensionMakeCall}
            />
          </Col>
        </Row>

        <h3 className={classes.title}>Dữ liệu cuộc gọi</h3>
        <Divider />

        <Row gutter={[16, 16]} className={classes.container}>
          <Col span={4}>
            <ReportExtensionCard
              title="Tổng cuộc gọi ra phát sinh"
              value={reportExtenstionData?.totalOutboundCall}
            />
          </Col>
          <Col span={4}>
            <ReportExtensionCard
              title="Tổng cuộc gọi trên Pack&Send"
              value={reportExtenstionData?.totalPackAndSendCall}
            />
          </Col>
          <Col span={4}>
            <ReportExtensionCard
              title="Tổng cuộc gọi vào"
              value={reportExtenstionData?.totalInboundCall}
            />
          </Col>
          <Col span={4}>
            <ReportExtensionCard
              title="Tổng cuộc gọi đơn vị trả lời"
              value={reportExtenstionData?.totalAnsweredInboundCall}
            />
          </Col>
          <Col span={4}>
            {isPostOfficeType ? (
              <ReportExtensionCard
                title={"Tổng cuộc gọi không trả lời"}
                value={reportExtenstionData?.totalNoAnsweredInboundCall}
              />
            ) : (
              <ReportExtensionCard
                title={"Tổng cuộc gọi nhỡ"}
                value={reportExtenstionData?.totalMissInboundCall}
              />
            )}
          </Col>
          <Col span={4}>
            <ReportExtensionCard
              title={
                isPostOfficeType
                  ? "Tổng cuộc gọi chuyển tiếp đi"
                  : "Tổng cuộc gọi chuyển tiếp đến"
              }
              value={reportExtenstionData?.totalTransferInboundCall}
            />
          </Col>
        </Row>
      </Card>

      <Card sx={{ mt: 1, overflow: "auto" }}>
        <Table
          columns={columns}
          data={reportExtenstionData?.detailReport}
          showBorder
        />
      </Card>

      <LoadingScreen loading={isFetching || isFetchingExport} />
    </ThemeProvider>
  );
};

export default ReportExension;
